import { type FC } from 'react'
import { observer } from 'mobx-react-lite'
import {
  Avatar,
  Dropdown,
  EnumDropdownItemVariant,
  Icon,
  type IDropdownItem,
  type IDropdownProps,
} from 'shared/ui'
import { type CannedMessage } from 'entities/CannedMessages/model/CannedMessage'
import { useSavedRepliesContext } from 'widgets/SavedReplies/context'

type ISavedReplyActionsProps = {
  savedReply: CannedMessage
  sizeTriggerButton?: IDropdownProps['sizeTriggerButton']
  placement?: IDropdownProps['placement']
  withoutAdd?: boolean
  tooltipParentElement?: HTMLDivElement | null
  collapsed?: boolean
}

export const SavedReplyActions: FC<ISavedReplyActionsProps> = observer(
  ({
    savedReply,
    sizeTriggerButton = 'small',
    placement = 'bottom-start',
    withoutAdd,
    tooltipParentElement,
    collapsed,
  }) => {
    const store = useSavedRepliesContext()
    const {
      setVisibleSavedRepliesActionsId,
      setSelectSavedReply,
      handleAddSavedReplies,
      handleEditSavedReplies,
      handleDuplicateSavedReplies,
      handleChangeVisibilitySavedReplies,
      handleDeleteSavedReplies,
      handleFavoriteSavedReplies,
      canManageSavedReplies,
      canManageAllSavedReplies,
      savedRepliesPermissionError,
      entities: {
        usersStore: { user },
      },
    } = store

    const replyOwner = store.entities.usersStore.getItem(savedReply.owner_id)
    const visibilityText = savedReply.isMyReply
      ? 'Myself only'
      : replyOwner
      ? `${replyOwner.full_name} only`
      : 'Deleted user only'

    const visibilityIcon = savedReply.isMyReply ? (
      user?.avatarInfo ? (
        <Avatar info={user.avatarInfo} size={16} />
      ) : (
        <Icon icon={'userLine'} />
      )
    ) : replyOwner?.avatarInfo ? (
      <Avatar info={replyOwner.avatarInfo} size={16} />
    ) : (
      <Icon icon={'userLine'} />
    )

    const ownerItems: IDropdownProps['items'] = [
      {
        id: 'add',
        label: 'Add to conversation',
        iconL: 'add',
      },
      {
        id: 'favorite',
        label: savedReply.is_favorite ? 'Unfavorite' : 'Favorite',
        iconL: savedReply.is_favorite ? 'starFilled1' : 'starLine',
      },
      {
        id: 'edit',
        label: 'Edit',
        iconL: 'edit',
        disabled: !canManageSavedReplies,
        tooltipProps: {
          label: savedRepliesPermissionError,
          placement: 'top',
        },
      },
      {
        id: 'duplicate',
        label: 'Duplicate',
        iconL: 'copy',
        divider: true,
        disabled: !canManageSavedReplies,
        tooltipProps: {
          label: savedRepliesPermissionError,
          placement: 'top',
        },
      },

      {
        id: 'visibility',
        wrapLabel: 'Visibility',
        label: savedReply.public ? 'All members' : visibilityText,
        iconL: savedReply.public ? 'users' : undefined,
        customIconL: user && !savedReply.public ? visibilityIcon : undefined,
        iconR: !canManageSavedReplies ? undefined : 'chevronRight',
        disabled: !canManageSavedReplies,
        tooltipProps: {
          label: savedRepliesPermissionError,
          placement: 'top',
        },
        divider: true,
        children: !canManageSavedReplies
          ? undefined
          : [
              {
                id: 'personal',
                label: visibilityText,
                customIconL: user ? visibilityIcon : undefined,
                activeValue: !savedReply.public,
                variant: EnumDropdownItemVariant.CheckedRight,
              },
              {
                id: 'everyone',
                label: 'All members',
                iconL: 'users',
                activeValue: savedReply.public,
                variant: EnumDropdownItemVariant.CheckedRight,
              },
            ],
      },
      {
        id: 'delete',
        label: 'Delete',
        iconL: 'delete',
        disabled: !canManageSavedReplies,
        tooltipProps: {
          label: savedRepliesPermissionError,
          placement: 'top',
        },
      },
    ]

    const teamItems: IDropdownProps['items'] = [
      {
        id: 'add',
        label: 'Add to conversation',
        iconL: 'add',
      },
      {
        id: 'favorite',
        label: savedReply.is_favorite ? 'Unfavorite' : 'Favorite',
        iconL: savedReply.is_favorite ? 'starFilled1' : 'starLine',
      },
      {
        id: 'duplicate',
        label: 'Duplicate',
        iconL: 'copy',
        disabled: !canManageSavedReplies,
        tooltipProps: {
          label: savedRepliesPermissionError,
          placement: 'top',
        },
      },
    ]

    const onChange = (item: IDropdownItem) => {
      if (item.id === 'add') {
        handleAddSavedReplies(savedReply)
      }
      if (item.id === 'edit') {
        handleEditSavedReplies(savedReply)
      }
      if (item.id === 'duplicate') {
        handleDuplicateSavedReplies(savedReply)
      }
      if (item.id === 'personal') {
        handleChangeVisibilitySavedReplies(savedReply, false)
      }
      if (item.id === 'everyone') {
        handleChangeVisibilitySavedReplies(savedReply, true)
      }
      if (item.id === 'delete') {
        handleDeleteSavedReplies(savedReply)
      }
      if (item.id === 'favorite') {
        handleFavoriteSavedReplies(savedReply)
      }
    }
    const onToggle = (value: boolean) => {
      if (value) {
        setSelectSavedReply(savedReply)
      }

      if (!withoutAdd) {
        setVisibleSavedRepliesActionsId(value ? savedReply.id : 0)
      }
    }

    if (collapsed) {
      return (
        <Dropdown
          ariaLabel={'SavedReplyActions'}
          icon={'moreHorizontal'}
          sizeTriggerButton={sizeTriggerButton}
          placement={placement}
          items={(savedReply.isMyReply || canManageAllSavedReplies ? ownerItems : teamItems).filter(
            (item) => (withoutAdd ? item.id !== 'add' : true)
          )}
          onToggle={onToggle}
          onChange={onChange}
          tooltipProps={{
            label: 'Manage',
            placement: 'top',
            PopperProps: {
              popperOptions: {
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: tooltipParentElement,
                      padding: 8,
                    },
                  },
                ],
              },
            },
          }}
        />
      )
    }

    return (
      <Dropdown
        ariaLabel={'SavedReplyActions'}
        icon={'moreHorizontal'}
        sizeTriggerButton={sizeTriggerButton}
        placement={placement}
        items={(savedReply.isMyReply ? ownerItems : teamItems).filter((item) =>
          withoutAdd ? item.id !== 'add' : true
        )}
        typeButton={'button'}
        buttonProps={{
          text: 'Manage',
          icon: 'settings',
          contained: 'secondary',
          size: 'medium',
          disabled: !canManageSavedReplies,
          tooltipProps: {
            label: savedRepliesPermissionError,
            placement: 'top',
          },
        }}
        onToggle={onToggle}
        onChange={onChange}
        tooltipProps={{
          label: 'Manage',
          placement: 'top',
          PopperProps: {
            popperOptions: {
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: tooltipParentElement,
                    padding: 8,
                  },
                },
              ],
            },
          },
        }}
      />
    )
  }
)
